/**
 * Shrink a photographed prescription in the browser before it is uploaded.
 *
 * Phone cameras produce 4-8 MB files; on a clinic-waiting-room connection that is
 * a minute of waiting and often a failed POST. We resize the long edge and step the
 * JPEG quality down until the file is under the target, leaving PDFs untouched.
 */
const MAX_EDGE = 1600;
const TARGET_BYTES = 1024 * 1024;
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5, 0.42];

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable image")); };
    img.src = url;
  });

const toBlob = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

export async function compressImage(file) {
  if (!file || !file.type.startsWith("image/")) return file;
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let best = null;
    for (const quality of QUALITY_STEPS) {
      best = await toBlob(canvas, quality);
      if (best && best.size <= TARGET_BYTES) break;
    }
    if (!best || best.size >= file.size) return file;
    return new File([best], (file.name || "prescription").replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
    });
  } catch {
    // A HEIC or otherwise undecodable photo still goes up as-is.
    return file;
  }
}

export const readableSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
